import { Component, EventEmitter, Input, Output, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { CheckboxModule } from 'primeng/checkbox';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { AiFieldBoundaryService } from '@6grain/masofa-monolith-api-client/ai-inference/api/aiFieldBoundary.service';
import { PolygonEditMapComponent } from '../polygon-edit-map/polygon-edit-map.component';

@Component({
  selector: 'app-field-boundary-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    TextareaModule,
    CheckboxModule,
    TabsModule,
    TooltipModule,
    PolygonEditMapComponent,
  ],
  templateUrl: './field-boundary-modal.component.html',
  styleUrls: ['./field-boundary-modal.component.scss'],
  providers: [MessageService],
})
export class FieldBoundaryModalComponent {
  private readonly aiFieldService = inject(AiFieldBoundaryService);
  private readonly translate = inject(TranslateService);
  private readonly messageService = inject(MessageService);

  @ViewChild('polygonMap') polygonMap?: PolygonEditMapComponent;
  @ViewChild('polygonMapSplit') polygonMapSplit?: PolygonEditMapComponent;
  @ViewChild('polygonMapTarget') polygonMapTarget?: PolygonEditMapComponent;

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() actionCompleted = new EventEmitter<void>();

  private _fieldData: any = null;
  @Input() set fieldData(value: any) {
    this._fieldData = value;
    if (value) {
      this.initModal(value);
    }
  }
  get fieldData() {
    return this._fieldData;
  }

  activeTabValue: string = '0';
  newFieldName: string = '';

  isStopList: boolean = false;
  stopListComment: string = '';

  currentPolygonPath: number[][] = [];
  editedPolygonPath: number[][] = [];
  intersectingFields: any[] = [];
  selectedTargetField: any = null;
  targetFieldPolygonPath: number[][] = [];
  isLoadingIntersecting: boolean = false;

  isSaving: boolean = false;
  isReplacing: boolean = false;
  isDeleting: boolean = false;

  onClose(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  centerMap(): void {
    if (this.activeTabValue === '0' && this.polygonMap) {
      this.polygonMap.fitBounds();
    } else if (this.activeTabValue === '1') {
      if (this.polygonMapSplit) {
        this.polygonMapSplit.fitBounds();
      }
      if (this.polygonMapTarget) {
        this.polygonMapTarget.fitBounds();
      }
    }
  }

  private initModal(data: any): void {
    const tField = this.translate.instant('FIELD');
    this.newFieldName = `${tField} ${data.cadastralNumber || ''}`;
    this.isStopList = false;
    this.stopListComment = '';

    this.currentPolygonPath = [];
    this.editedPolygonPath = [];

    this.activeTabValue = '0';
    this.intersectingFields = [];
    this.selectedTargetField = null;
    this.targetFieldPolygonPath = [];
    const geometry = data.polygon || data.geometry;
    if (geometry && geometry.coordinates) {
      let ring: any[] = [];
      if (geometry.type === 'Polygon') {
        ring = geometry.coordinates[0];
      } else if (geometry.type === 'MultiPolygon') {
        ring = geometry.coordinates[0][0];
      }

      if (Array.isArray(ring) && ring.length > 0) {
        if (Array.isArray(ring[0])) {
          this.currentPolygonPath = ring.map((p: any) => [p[1], p[0]]);
          this.editedPolygonPath = [...this.currentPolygonPath];
        }
      }
    }
  }

  onPolygonEdited(newCoords: number[][]): void {
    this.editedPolygonPath = newCoords;
  }

  onTabChange(value: string | number): void {
    this.activeTabValue = String(value);
    if (this.activeTabValue === '1' && this.intersectingFields.length === 0) {
      this.loadIntersectingFields();
    }
  }

  loadIntersectingFields(): void {
    if (!this.fieldData) return;
    this.isLoadingIntersecting = true;
    this.intersectingFields = [];
    this.selectedTargetField = null;

    let finalPath = this.getFinalPolygonPath();
    const ring = finalPath.map((p) => [p[1], p[0]]);
    if (ring.length > 0 && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) {
      ring.push(ring[0]);
    }

    const payload = {
      aiBoundaryId: this.fieldData.id,
      polygon: { type: 'Polygon', coordinates: [ring] },
    };
    this.aiFieldService.aiInferenceAiFieldBoundaryGetIntersectingFieldsPost(payload as any).subscribe({
      next: (res: any) => {
        if (Array.isArray(res)) {
          this.intersectingFields = res;
          if (this.intersectingFields.length > 0) {
            this.selectTargetField(this.intersectingFields[0]);
          }
        } else {
          this.intersectingFields = [];
        }
        this.isLoadingIntersecting = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('ERROR'),
          detail: this.translate.instant('MSG_FAILED_LOAD_INTERSECTING'),
        });
        this.isLoadingIntersecting = false;
      },
    });
  }

  selectTargetField(field: any): void {
    this.selectedTargetField = field;
    this.parseTargetFieldGeometry(field.polygon);
  }

  parseTargetFieldGeometry(geometry: any): void {
    this.targetFieldPolygonPath = [];
    if (geometry && geometry.coordinates) {
      let ring: any[] = [];
      if (geometry.type === 'Polygon') ring = geometry.coordinates[0];
      else if (geometry.type === 'MultiPolygon') ring = geometry.coordinates[0][0];
      if (Array.isArray(ring) && ring.length > 0 && Array.isArray(ring[0])) {
        this.targetFieldPolygonPath = ring.map((p: any) => [p[1], p[0]]);
      }
    }
  }

  getFinalPolygonPath(): number[][] {
    if (this.editedPolygonPath && this.editedPolygonPath.length > 0) {
      return this.editedPolygonPath;
    }
    return this.currentPolygonPath;
  }

  replaceAiField(): void {
    if (!this.fieldData || !this.selectedTargetField) return;
    this.isReplacing = true;

    let finalPath = this.getFinalPolygonPath();
    const ring = finalPath.map((p) => [p[1], p[0]]);
    if (ring.length > 0 && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) {
      ring.push(ring[0]);
    }

    const payload = {
      aiBoundaryId: this.fieldData.id,
      targetFieldId: this.selectedTargetField.id,
      polygon: { type: 'Polygon', coordinates: [ring] },
    };
    this.aiFieldService.aiInferenceAiFieldBoundaryReplaceBoundaryPost(payload).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('SUCCESS'),
          detail: this.translate.instant('MSG_FIELD_REPLACED_SUCCESS'),
        });
        this.completeAction();
        this.isReplacing = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('ERROR'),
          detail: this.translate.instant('MSG_FAILED_REPLACE_FIELD'),
        });
        this.isReplacing = false;
      },
    });
  }

  deleteAiField(): void {
    if (!this.fieldData || !this.fieldData.id) return;
    this.isDeleting = true;
    this.aiFieldService.aiInferenceAiFieldBoundaryDeleteIdDelete(this.fieldData.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('SUCCESS'),
          detail: this.translate.instant('MSG_FIELD_DELETED_SUCCESS'),
        });
        this.completeAction();
        this.isDeleting = false;
      },
      error: (err: any) => {
        console.error('Error deleting field:', err);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('ERROR'),
          detail: this.translate.instant('MSG_FAILED_DELETE_FIELD'),
        });
        this.isDeleting = false;
      },
    });
  }

  saveAiField(): void {
    if (!this.fieldData) return;
    this.isSaving = true;

    let finalPath = this.getFinalPolygonPath();
    const ring = finalPath.map((p) => [p[1], p[0]]);

    if (ring.length > 0) {
      const first = ring[0];
      const last = ring[ring.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        ring.push(first);
      }
    }

    const updatedGeometry = {
      type: 'Polygon',
      coordinates: [ring],
    };
    const payload: any = {
      aiBoundaryId: this.fieldData.id,
      name: this.newFieldName,
      polygon: updatedGeometry,
      isStopList: this.isStopList,
      stopListComment: this.isStopList ? this.stopListComment : null,
    };

    this.aiFieldService.aiInferenceAiFieldBoundaryApproveBoundaryPost(payload).subscribe({
      next: () => {
        const msgKey = this.isStopList ? 'MSG_STOP_LIST_SAVED' : 'MSG_FIELD_APPROVED_SUCCESS';
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('SUCCESS'),
          detail: this.translate.instant(msgKey),
        });
        this.completeAction();
        this.isSaving = false;
      },
      error: (err: any) => {
        console.error(err);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('ERROR'),
          detail: this.translate.instant('MSG_FAILED_APPROVE_FIELD'),
        });
        this.isSaving = false;
      },
    });
  }

  private completeAction(): void {
    this.onClose();
    this.actionCompleted.emit();
  }
}
